"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations, useLocale } from "next-intl";
import {
  Home,
  BarChart3,
  Play,
  Terminal,
  ChevronLeft,
  Database,
  Globe,
  Bot,
  Users,
  ShieldCheck,
  FileText,
  Library,
  ServerCog,
  KeyRound,
  Sliders,
  BookOpen,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { governanceNavigation, governancePageTitle } from "@/lib/governance-navigation";
import { useSidebar } from "@/contexts/sidebar-context";
import { useAuth } from "@/contexts/auth-context";
import { useOrganizationAccess } from "@/components/workspaces/use-organization-access";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const navItems = [
  {
    section: "overview",
    items: [
      { href: "", icon: Home, labelKey: "home", protected: false },
      { href: "/analytics", icon: BarChart3, labelKey: "analytics", protected: true },
    ],
  },
  {
    section: "operations",
    items: [
      { href: "/runs", icon: Play, labelKey: "runs", protected: true },
      { href: "/logs", icon: Terminal, labelKey: "logs", protected: true },
    ],
  },
  {
    section: "management",
    items: [
      // domains/library are visible to any authenticated user — the
      // baseline grants domains.manage_own to everyone, so a plain user's
      // trip there is genuinely useful, unlike the always-admin-only items
      // below. The rest of these were previously shown to every
      // authenticated user regardless of role (confirmed: none had
      // adminOnly set) even though ai_models.manage/agents.manage/
      // document_keywords.manage are always-admin-only, is_api_key_eligible
      // =false, and absent from the USER baseline — a plain user's click
      // led straight to a page that 403s on every real request. Gated on
      // the matching capability now (Phase 6 of
      // [[rbac-permission-overhaul-initiative]]) — UX only, the backend's
      // require_permission(...) is what actually enforces this either way.
      { href: "/organizations", icon: Database, labelKey: "organizations", protected: true },
      { href: "/tenants", icon: Database, labelKey: "tenants", protected: true, requiredCapability: "tenants.manage" },
      { href: "/domain-access", icon: ShieldCheck, labelKey: "domainAccess", protected: true },
      { href: "/machine-identities", icon: KeyRound, labelKey: "domainAccess", protected: true },
      { href: "/prompt-governance", icon: BookOpen, labelKey: "domainAccess", protected: true },
      { href: "/config-governance", icon: Sliders, labelKey: "domainAccess", protected: true },
      { href: "/domain-egress", icon: ShieldCheck, labelKey: "domainAccess", protected: true },
      { href: "/content-safety", icon: ShieldCheck, labelKey: "domainAccess", protected: true },
      { href: "/feedback-curation", icon: BookOpen, labelKey: "domainAccess", protected: true },
      { href: "/library", icon: Library, labelKey: "library", protected: true },
      { href: "/domains", icon: Globe, labelKey: "domains", protected: true },
      { href: "/ai-models", icon: Bot, labelKey: "aiModels", protected: true, requiredCapability: "ai_models.manage" },
      { href: "/agents", icon: Users, labelKey: "agents", protected: true, customLabel: "Agents", requiredCapability: "agents.manage" },
      { href: "/document-keywords", icon: FileText, labelKey: "documentKeywords", protected: true, customLabel: "Document Keywords", requiredCapability: "document_keywords.manage" },
      { href: "/workers", icon: ServerCog, labelKey: "workers", protected: true, requiredCapability: "workers.view" },
      { href: "/users", icon: ShieldCheck, labelKey: "users", protected: true, customLabel: "User Administration", requiredCapability: "users.view" },
      { href: "/roles", icon: KeyRound, labelKey: "roles", protected: true, customLabel: "Role Administration", requiredCapability: "roles.manage" },
      { href: "/agent-model-pool", icon: Sliders, labelKey: "agentModelPool", protected: true },
      { href: "/knowledge-base", icon: BookOpen, labelKey: "knowledgeBase", protected: true, requiredCapability: "knowledge_base.manage" },
    ],
  },
];

export function Sidebar() {
  const pathname = usePathname();
  const locale = useLocale();
  const t = useTranslations("nav");
  const tSections = useTranslations("sections");
  const { isCollapsed, toggleCollapsed, isMobileOpen, closeMobile } = useSidebar();
  const { isAuthenticated, hasCapability } = useAuth();
  const organizationAccess = useOrganizationAccess();
  const isRTL = locale === "ar";

  const isActive = (href: string) => {
    const fullPath = `/${locale}${href}`;
    if (href === "") {
      return pathname === `/${locale}` || pathname === `/${locale}/`;
    }
    return pathname.startsWith(fullPath);
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={closeMobile}
        />
      )}

      <aside
        className={cn(
          "fixed top-0 z-50 h-screen bg-sidebar border-sidebar-border flex flex-col transition-all duration-300",
          isRTL ? "right-0 border-l" : "left-0 border-r",
          isCollapsed ? "w-16" : "w-64",
          isMobileOpen
            ? "translate-x-0"
            : isRTL
            ? "translate-x-full lg:translate-x-0"
            : "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* Header */}
        <div className="flex h-16 items-center justify-between px-4 border-b border-sidebar-border">
          <Link
            href={`/${locale}`}
            className="flex items-center gap-3"
            onClick={closeMobile}
          >
            <img 
              src="/image.png" 
              alt="DigiIntellect" 
              className={cn(
                "transition-all duration-300",
                isCollapsed ? "h-8 w-8 min-w-[32px]" : "h-9 w-auto"
              )} 
            />
          </Link>
        </div>

        {/* Toggle button */}
        <button
          onClick={toggleCollapsed}
          className={cn(
            "absolute top-20 hidden lg:flex h-6 w-6 items-center justify-center rounded-full border border-sidebar-border bg-sidebar text-sidebar-foreground hover:bg-sidebar-accent transition-colors",
            isRTL ? "-left-3" : "-right-3"
          )}
        >
          <ChevronLeft
            className={cn(
              "h-4 w-4 transition-transform",
              isCollapsed && (isRTL ? "-rotate-180" : "rotate-180")
            )}
          />
        </button>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <TooltipProvider delayDuration={0}>
            {navItems.map((section) => (
              <div key={section.section} className="mb-6">
                {!isCollapsed && (
                  <p className="mb-2 px-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {tSections(section.section)}
                  </p>
                )}
                <ul className="space-y-1">
                  {section.items.map((item) => {
                    const requiredCapability = (item as any).requiredCapability as string | undefined;
                    if (item.href === "/organizations" && !organizationAccess.current?.can_administer) return null;
                    if (requiredCapability && !hasCapability(requiredCapability)) return null;
                    const anyCapabilities = governanceNavigation[item.href]?.capabilities;
                    if (anyCapabilities && !anyCapabilities.some(key => hasCapability(key))) return null;
                    const label = governancePageTitle(item.href, locale) || (item as any).customLabel || t(item.labelKey);
                    const Icon = item.icon;
                    const active = isActive(item.href);
                    const locked = item.protected && !isAuthenticated;

                    const linkContent = (
                      <Link
                        href={locked ? "#" : `/${locale}${item.href}`}
                        onClick={(e) => {
                          if (locked) {
                            e.preventDefault();
                          } else {
                            closeMobile();
                          }
                        }}
                        className={cn(
                          "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                          active
                            ? "bg-sidebar-accent text-sidebar-accent-foreground"
                            : "text-sidebar-foreground hover:bg-sidebar-accent/50",
                          locked && "opacity-50 cursor-not-allowed",
                          isCollapsed && "justify-center"
                        )}
                      >
                        <Icon className="h-5 w-5 shrink-0" />
                        {!isCollapsed && <span>{label}</span>}
                      </Link>
                    );

                    if (isCollapsed) {
                      return (
                        <li key={item.href}>
                          <Tooltip>
                            <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                            <TooltipContent side={isRTL ? "left" : "right"}>
                              {label}
                            </TooltipContent>
                          </Tooltip>
                        </li>
                      );
                    }

                    return <li key={item.href}>{linkContent}</li>;
                  })}
                </ul>
              </div>
            ))}
          </TooltipProvider>
        </nav>
      </aside>
    </>
  );
}
